/**
 * Ops users roster — grant/revoke/change ops access (owner only).
 */
import { useState, useEffect } from 'react';
import { api } from '../../api';
import ReasonField from '../../components/ops_admin/ReasonField';

export default function OpsUsers({ opsRole }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGrant, setShowGrant] = useState(false);

  async function fetchUsers() {
    setLoading(true);
    try {
      const d = await api.get('/ops_admin/ops_users');
      setUsers(d.ops_users || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => { fetchUsers(); }, []);

  if (opsRole !== 'owner') {
    return <p className="info-text">Only owners can manage ops users.</p>;
  }

  if (loading) return <p className="loading-text">Loading...</p>;
  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="ops-users-page">
      <div className="ops-page-header">
        <h1>Ops Users</h1>
        <button className="btn btn-primary" onClick={() => setShowGrant(true)}>+ Grant Access</button>
      </div>

      <table className="ops-table">
        <thead>
          <tr><th>Email</th><th>Role</th><th>Granted By</th><th>Granted</th><th>Last Active</th><th>Notes</th><th></th></tr>
        </thead>
        <tbody>
          {users.length === 0 && <tr><td colSpan={7} className="empty-row">No ops users</td></tr>}
          {users.map(u => (
            <UserRow key={u.user_id} user={u} onRefresh={fetchUsers} />
          ))}
        </tbody>
      </table>

      {showGrant && (
        <GrantModal onClose={() => setShowGrant(false)} onCreated={() => { setShowGrant(false); fetchUsers(); }} />
      )}
    </div>
  );
}

function UserRow({ user, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [newRole, setNewRole] = useState(user.role);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleChange() {
    setSaving(true);
    try {
      await api.patch(`/ops_admin/ops_users/${user.user_id}`, { role: newRole, reason });
      setEditing(false); setReason('');
      onRefresh();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function handleRevoke() {
    if (!reason.trim()) { setError('Reason required'); return; }
    setSaving(true);
    try {
      await api.del(`/ops_admin/ops_users/${user.user_id}?reason=${encodeURIComponent(reason)}`);
      onRefresh();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <tr>
      <td>{user.email}</td>
      <td>
        {editing ? (
          <select className="form-input form-input-sm" value={newRole} onChange={e => setNewRole(e.target.value)}>
            {['owner', 'admin', 'legal_contact', 'support', 'readonly'].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        ) : <span className="role-badge">{user.role}</span>}
      </td>
      <td>{user.granted_by || '—'}</td>
      <td>{user.granted_at ? new Date(user.granted_at).toLocaleDateString() : '—'}</td>
      <td>{user.last_active_at ? new Date(user.last_active_at).toLocaleDateString() : 'Never'}</td>
      <td>{user.notes || '—'}</td>
      <td>
        {editing ? (
          <div>
            <ReasonField value={reason} onChange={setReason} placeholder="Reason..." />
            {error && <p className="error-text">{error}</p>}
            <button className="btn btn-sm btn-primary" onClick={handleChange} disabled={saving}>Save</button>
            <button className="btn btn-sm btn-danger" onClick={handleRevoke} disabled={saving}>Revoke</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        ) : (
          <button className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>Edit</button>
        )}
      </td>
    </tr>
  );
}

function GrantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ user_id: '', email: '', role: 'support', notes: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await api.post('/ops_admin/ops_users', form);
      onCreated();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>Grant Ops Access</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Firebase UID *</label>
            <input className="form-input" required value={form.user_id}
              onChange={e => setForm(f => ({...f, user_id: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input className="form-input" type="email" required value={form.email}
              onChange={e => setForm(f => ({...f, email: e.target.value}))} />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select className="form-input" value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}>
              {['owner', 'admin', 'legal_contact', 'support', 'readonly'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-input" value={form.notes}
              onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
          </div>
          <ReasonField value={form.reason} onChange={r => setForm(f => ({...f, reason: r}))} />
          {error && <p className="error-text">{error}</p>}
          <div className="button-row">
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Granting...' : 'Grant Access'}</button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
